import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import ResizableComponent from "../../Layouts/ResizableComponent";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import Navbar from "../../Layouts/Navbar/Navbar";
import ErrorMessage from "../../components/FormComponents/ErrorMessage";
import Button from "../../components/FormComponents/Button";
import LeftSideBar from "../../components/SideBar/LeftSideBar";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { useTableContext } from "../../context/TableContext";
import { useTreeContext } from "../../context/TreeDataContext";
import { useEntityContext } from "../../context/EntityContext";
import { useAppBlocksContext } from "../../context/AppBlocksContext";
import { useNavigationContext } from "../../context/NavigationContext";
import { deleteFolderOfFile, editFolderOrFile } from "../../utils/treeData/folderEditorUtils";
import { toastSuccess } from "../../utils/toastUtils";
import { useModalContext } from "../../context/ModalContext";
import { getTreeDataForStateMachine } from "../../utils/treeData/treeDataUtils";
import CreateEntityModal from "./components/CreateEntityModal";
import fileFolderConfig, { getFileInfo } from "../../commonFunctions/fileFolderConfig";
import { useFormContext } from "../../context/FormContext";
import Loader from "../../Layouts/Loader/Loader";
import "./Entity.css";

const EntitySection = () => {
  const [isLoader, setIsLoader] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { getAllTableData, getOneTableData } = useTableContext();
  const { entityExplorerData, setEntityExplorerData, setStateMachineExplorerData } = useTreeContext();
  const { selectedEntity, setSelectedEntity, fetchAllEntities } = useEntityContext();
  const { fetchAllApplicationBlock } = useAppBlocksContext();
  const { saveChangesAndNavigate } = useNavigationContext();
  const { getUserConfirmationForDelete } = useModalContext();
  const { fetchAllForms } = useFormContext();

  const { entityId } = useParams();
  const navigate = useNavigate();

  const updateSmExplorerData = async () => {
    try {
      const { data } = await axiosInstance.get(`/state-machines`);
      const treeData = getTreeDataForStateMachine("all", data);
      setStateMachineExplorerData(treeData);
    } catch (error) {
      console.error("Error fetching state machines:", error);
    }
  };

  const callbackFunction = (id) => {
    if (id === selectedEntity.id) return;
    // navigate(`./${id}`);
    saveChangesAndNavigate(id);
  };

  const handleDelete = async (folder) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(folder.name);
      if (!userConfirmed) return;

      const { message } = await axiosInstance.delete(`/entity/${folder.id}`);
      const newTree = deleteFolderOfFile(entityExplorerData, folder);
      setEntityExplorerData(newTree);
      if (folder.id === selectedEntity?.id) {
        navigate(`./`);
      }
      updateSmExplorerData();
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = async (folder, newName) => {
    try {
      const config = {
        name: newName
      };
      const { message } = await axiosInstance.put(`/entity/${folder.id}`, config);

      const newTree = editFolderOrFile(entityExplorerData, folder, newName);
      setEntityExplorerData(newTree);
      updateSmExplorerData();
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const createEntity = async (entityData) => {
    try {
      const entityConfig = {
        name: entityData.name,
        is_offline: entityData.is_offline
      };

      const { data, message } = await axiosInstance.post(`/entity`, entityConfig);
      await getOneTableData(data.sm_id);
      const newItem = fileFolderConfig.getFileConfig(
        data.id,
        data.name,
        "entities",
        false,
        true,
        getFileInfo("entity", entityConfig)
      );
      setEntityExplorerData([...entityExplorerData, newItem]);
      navigate(`./${data.id}`);
      updateSmExplorerData();
      toastSuccess(message);
      await fetchAllForms();
    } catch (error) {
      handleApiError(error);
    }
  };

  const initialFetching = async () => {
    setIsLoader(true);
    setIsError(false);
    try {
      await getAllTableData();
      if (entityExplorerData && entityExplorerData.length > 0) {
        setEntityExplorerData(entityExplorerData);
        if (selectedEntity.id && !entityId) {
          setSelectedEntity({ id: "" });
        }
      } else {
        await fetchAllEntities();
        await fetchAllApplicationBlock();
      }
    } catch (error) {
      handleApiError(error);
      setIsError(true);
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    initialFetching();

    // Cleanup function to reset selectedEntity when component unmounts
    return () => {
      setSelectedEntity({ id: "" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section className="main_section">
        <div className="sub_section">
          <ResizableComponent
            resizeSide="right"
            storageKey="left_sidebar_size"
            defaultSize={244}
            range={[244, 400]} // Specify the range as [minSize, maxSize]
            collapsible={true}
          >
            <HeaderModes />
            <div className="d_flex">
              <Navbar />
              {isError ? (
                <div className="d_flex align_center justify_center flex_column">
                  <ErrorMessage className={"text_center mb_3"}>Failed to load Entities. Please try again</ErrorMessage>
                  <Button onClick={initialFetching}>Retry</Button>
                </div>
              ) : (
                <LeftSideBar
                  explorerData={entityExplorerData}
                  setExplorerData={setEntityExplorerData}
                  selectedScreen={selectedEntity}
                  callbackFunction={callbackFunction}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  type="Entity"
                  isLoader={isLoader}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <MaterialIcon name="add" className="sidebar_icons" onClick={() => setIsModalOpen(true)} />
                  </div>
                </LeftSideBar>
              )}
            </div>
          </ResizableComponent>
          {isLoader ? <Loader /> : <Outlet />}
        </div>
      </section>
      <BuilderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} modalName="Create Entity">
        <CreateEntityModal createEntity={createEntity} setIsModalOpen={setIsModalOpen} />
      </BuilderModal>
    </>
  );
};

export default EntitySection;
